import { useEffect, useState } from "react"

import {
  Button,
  Input,
  Loader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@coval/ui"
import { Check, Languages } from "lucide-react"

import { logger } from "@/lib/logger"

import {
  PERSONA_VOICE_DESIGN_LANGUAGES,
  type PersonaVoiceDesignLanguage,
  type usePersonaVoiceDesign,
} from "../hooks/usePersonaVoiceDesign"
import type { PersonaCustomVoiceOption } from "../types"
import { AudioPreviewButton } from "./AudioPreviewButton"

const log = logger("PersonaCustomVoiceFields")

const MAX_DESCRIPTION_LENGTH = 500
const MAX_CUSTOM_VOICE_NAME_LENGTH = 100

type PersonaVoiceDesignController = ReturnType<typeof usePersonaVoiceDesign>

interface PersonaVoicePreviewProps {
  voiceId: string
  label: string
  previewUrl?: string
  previewFailed: boolean
  getPreviewUrl: (voiceId: string) => string | undefined
  currentlyPlayingAudioId?: string | null
  onPlayingChange: (playingId: string | null) => void
}

function PersonaVoicePreview({
  voiceId,
  label,
  previewUrl,
  previewFailed,
  getPreviewUrl,
  currentlyPlayingAudioId,
  onPlayingChange,
}: PersonaVoicePreviewProps) {
  useEffect(() => {
    if (!previewUrl && !previewFailed) {
      getPreviewUrl(voiceId)
    }
  }, [voiceId, getPreviewUrl, previewFailed, previewUrl])

  if (previewFailed) {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => getPreviewUrl(voiceId)}
      >
        Retry preview
      </Button>
    )
  }

  if (!previewUrl) {
    return (
      <span
        className="inline-flex h-8 w-8 items-center justify-center"
        aria-label={`Loading ${label} preview`}
      >
        <Loader size="sm" label={`Loading ${label} preview`} />
      </span>
    )
  }

  return (
    <AudioPreviewButton
      previewUrl={previewUrl}
      audioLabel={label}
      currentlyPlayingId={currentlyPlayingAudioId}
      onPlayingChange={onPlayingChange}
    />
  )
}

function PersonaVoiceCandidateCard({
  embeddingId,
  index,
  previewUrl,
  previewFailed,
  getPreviewUrl,
  isSelected,
  isDisabled,
  currentlyPlayingAudioId,
  onPlayingChange,
  onSelect,
}: {
  embeddingId: string
  index: number
  previewUrl?: string
  previewFailed: boolean
  getPreviewUrl: (voiceId: string) => string | undefined
  isSelected: boolean
  isDisabled: boolean
  currentlyPlayingAudioId?: string | null
  onPlayingChange: (playingId: string | null) => void
  onSelect: () => void
}) {
  const candidateLabel = `Option ${index + 1}`

  return (
    <div
      className={`flex min-w-0 items-center gap-3 rounded-md border p-3 transition-colors ${
        isSelected
          ? "border-coval-border-active bg-surface-0"
          : "border-coval-border hover:border-coval-border-active"
      }`}
    >
      <PersonaVoicePreview
        voiceId={embeddingId}
        label={candidateLabel}
        previewUrl={previewUrl}
        previewFailed={previewFailed}
        getPreviewUrl={getPreviewUrl}
        currentlyPlayingAudioId={currentlyPlayingAudioId}
        onPlayingChange={onPlayingChange}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-coval-text">
          {candidateLabel}
        </div>
        <div className="text-xs text-coval-text-muted">Voice preview</div>
      </div>
      <Button
        type="button"
        variant="secondary"
        disabled={isDisabled}
        aria-pressed={isSelected}
        aria-label={`Choose ${candidateLabel}`}
        onClick={onSelect}
      >
        {isSelected && <Check className="size-4" />}
        {isSelected ? "Selected" : "Choose"}
      </Button>
    </div>
  )
}

type VoiceDesignStep = "details" | "choose"

export function PersonaCustomVoiceFields({
  currentlyPlayingAudioId,
  onPlayingChange,
  voiceDesign,
  onCreated,
  onCancel,
}: {
  currentlyPlayingAudioId: string | null
  onPlayingChange: (playingId: string | null) => void
  voiceDesign: PersonaVoiceDesignController
  onCreated: (voice: PersonaCustomVoiceOption) => void
  onCancel: () => void
}) {
  const {
    candidates,
    isGenerating,
    keepingEmbeddingId,
    previewUrls,
    previewErrorIds,
    getPreviewUrl,
    generateCandidates,
    keepCandidate,
  } = voiceDesign
  const [step, setStep] = useState<VoiceDesignStep>("details")
  const [voiceName, setVoiceName] = useState("")
  const [description, setDescription] = useState("")
  const [language, setLanguage] = useState<PersonaVoiceDesignLanguage>("en")
  const [selectedEmbeddingId, setSelectedEmbeddingId] = useState<string | null>(
    null,
  )
  const [shouldAdvanceAfterGeneration, setShouldAdvanceAfterGeneration] =
    useState(false)

  useEffect(() => {
    if (!shouldAdvanceAfterGeneration || isGenerating) {
      return
    }

    setShouldAdvanceAfterGeneration(false)
    if (candidates.length > 0) {
      setStep("choose")
    }
  }, [candidates.length, isGenerating, shouldAdvanceAfterGeneration])

  const handleGenerate = async () => {
    const trimmedDescription = description.trim()
    if (!trimmedDescription) {
      return
    }
    setSelectedEmbeddingId(null)
    setShouldAdvanceAfterGeneration(true)
    await generateCandidates(trimmedDescription, language)
  }

  const handleCreate = async () => {
    if (!selectedEmbeddingId) {
      return
    }

    const trimmedDescription = description.trim()
    const trimmedVoiceName = voiceName.trim()
    if (!trimmedVoiceName) {
      return
    }

    try {
      const kept = await keepCandidate(
        selectedEmbeddingId,
        trimmedVoiceName,
        trimmedDescription,
      )
      const createdVoice: PersonaCustomVoiceOption = {
        id: kept.voice_id,
        name: kept.name || trimmedVoiceName,
        description: trimmedDescription,
        language,
      }
      onPlayingChange(null)
      toast.success({
        title: "Voice created",
        description: "Your new voice is selected and ready to use.",
      })
      onCreated(createdVoice)
    } catch (error) {
      log.error("Error creating a persona voice", error)
      toast.error({
        error,
        fallback: "Could not create that voice. Please try again.",
        title: "Error creating voice",
      })
    }
  }

  const hasCandidates = candidates.length > 0
  const isKeeping = keepingEmbeddingId !== null

  return (
    <div>
      {step === "details" ? (
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-coval-text">
              Voice Details
            </h3>
            <p className="mt-1 text-sm text-coval-text-muted">
              Choose a language and describe how the voice should sound.
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="persona-custom-voice-language"
              className="text-sm font-medium text-coval-text"
            >
              Language
            </label>
            <Select
              value={language}
              onValueChange={(value) => {
                if (value) {
                  setLanguage(value as PersonaVoiceDesignLanguage)
                }
              }}
            >
              <SelectTrigger
                id="persona-custom-voice-language"
                aria-label="Voice language"
                className="w-52"
              >
                <div className="flex items-center gap-1.5">
                  <Languages className="size-4 shrink-0" />
                  <SelectValue placeholder="Language" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {PERSONA_VOICE_DESIGN_LANGUAGES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="persona-custom-voice-description"
              className="text-sm font-medium text-coval-text"
            >
              Description
            </label>
            <Textarea
              id="persona-custom-voice-description"
              aria-label="Voice description"
              value={description}
              maxLength={MAX_DESCRIPTION_LENGTH}
              rows={4}
              className="resize-none overflow-y-auto"
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe how you want the voice to sound."
            />
            <div className="text-right text-xs text-coval-text-muted">
              {description.length} / {MAX_DESCRIPTION_LENGTH}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-coval-border pt-4">
            <Button
              type="button"
              variant="ghost"
              disabled={isGenerating}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              loading={isGenerating}
              disabled={!description.trim() || isKeeping}
              onClick={() => void handleGenerate()}
            >
              Generate
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-coval-text">
              Choose a Voice
            </h3>
            <p className="mt-1 text-sm text-coval-text-muted">
              Pick the option that sounds right.
            </p>
          </div>

          <div className="space-y-2">
            {hasCandidates &&
              candidates.map((candidateEmbeddingId, index) => (
                <PersonaVoiceCandidateCard
                  key={candidateEmbeddingId}
                  embeddingId={candidateEmbeddingId}
                  index={index}
                  previewUrl={previewUrls.get(candidateEmbeddingId)}
                  previewFailed={previewErrorIds.has(candidateEmbeddingId)}
                  getPreviewUrl={getPreviewUrl}
                  isSelected={selectedEmbeddingId === candidateEmbeddingId}
                  isDisabled={isKeeping}
                  currentlyPlayingAudioId={currentlyPlayingAudioId}
                  onPlayingChange={onPlayingChange}
                  onSelect={() => setSelectedEmbeddingId(candidateEmbeddingId)}
                />
              ))}
          </div>

          <div className="space-y-2 border-t border-coval-border pt-4">
            <label
              htmlFor="persona-custom-voice-name"
              className="text-sm font-medium text-coval-text"
            >
              Voice name
            </label>
            <p className="text-sm text-coval-text-muted">
              Give your voice a name so it’s easy to find in your voice library.
            </p>
            <Input
              id="persona-custom-voice-name"
              aria-label="Voice name"
              value={voiceName}
              maxLength={MAX_CUSTOM_VOICE_NAME_LENGTH}
              onChange={(event) => setVoiceName(event.target.value)}
            />
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-coval-border pt-4">
            <Button
              type="button"
              variant="ghost"
              disabled={isKeeping}
              onClick={() => {
                onPlayingChange(null)
                setStep("details")
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              loading={isKeeping}
              disabled={!selectedEmbeddingId || !voiceName.trim()}
              onClick={() => void handleCreate()}
            >
              Create voice
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
